import { selectedAccommodationId } from '@/layouts/queries'
import { useQuery, useQueryClient, useMutation } from '@tanstack/vue-query'
import { computed, unref } from 'vue'

import apis from '@/services'

export function useAvailableBookingSites() {
  const { isLoading, data, isFetching, isError } = useQuery({
    queryKey: ['RIAvailableBookingSites', selectedAccommodationId],
    queryFn: async ({ queryKey }) => await apis.rateInsights.getAvailableChannels({ accommodationId: queryKey[1] }),
  })

  const bookingSites = computed(() =>
    data?.value?.bookingSites
      ? data?.value?.bookingSites?.map(item => {
          const partner = data.value.partnersData?.advertiserDetails.find(partner => partner.nsid.id === item.id)
          return {
            ...item,
            fullName: partner?.translatedName.value,
          }
        })
      : []
  )

  return {
    isBookingSitesLoading: isLoading,
    bookingSites,
    isBookingSitesFetching: isFetching,
    isBookingSitesError: isError,
  }
}

export function useSelectedBookingSites() {
  const { isLoading, data, isFetching, isError } = useQuery({
    queryKey: ['RISelectedBookingSites', selectedAccommodationId],
    queryFn: async ({ queryKey }) => await apis.rateInsights.getSelectedChannels({ accommodationId: queryKey[1] }),
    retryOnMount: false,
  })

  const selectedChannels = computed(() =>
    data?.value?.selectedChannels !== undefined
      ? data?.value?.selectedChannels?.map(item => {
          const partner = data.value.partnersData?.advertiserDetails.find(partner => partner.nsid.id === item.id)
          return {
            ...item,
            fullName: partner?.translatedName.value,
            logo: partner?.logo,
          }
        })
      : []
  )

  return {
    isSelectedChannelsLoading: isLoading,
    selectedChannels,
    isSelectedChannelsFetching: isFetching,
    isSelectedChannelsError: isError,
  }
}

export function useCompetitors(accommodationId) {
  const { isLoading, data, isFetching, isError } = useQuery({
    queryKey: ['RICompetitors', accommodationId],
    enabled: !!unref(accommodationId),
    queryFn: ({ queryKey }) => apis.rateInsights.getCompetitors({ accommodationId: queryKey[1] }),
    retryOnMount: false,
  })

  const competitors = computed(() =>
    data?.value?.data === undefined || !Array.isArray(data.value.data) ? [] : data.value.data
  )
  const hasCompetitors = computed(() => Boolean(competitors.value.length))

  return {
    isCompetitorsLoading: isLoading,
    competitors,
    isCompetitorsFetching: isFetching,
    isCompetitorsError: isError,
    hasCompetitors,
  }
}

export function useRates(params) {
  const isQueryEnabled = computed(
    () =>
      !!params.marketRate.value &&
      !!params.aggregation.value &&
      !!params.channels.value &&
      !!params.competitors.value &&
      !!params.from.value &&
      !!params.to.value
  )

  const { isLoading, data, isFetching } = useQuery({
    queryKey: [
      'RIRates',
      selectedAccommodationId,
      params.aggregation,
      params.channels,
      params.competitors,
      params.currency,
      params.from,
      params.to,
      params.marketRate,
    ],
    enabled: isQueryEnabled,
    queryFn: ({ queryKey }) =>
      apis.rateInsights.getRates({
        accommodationId: queryKey[1],
        params: {
          aggregationInterval: queryKey[2],
          channels: queryKey[3],
          competitors: queryKey[4],
          currencyCode: queryKey[5],
          startDate: queryKey[6],
          endDate: queryKey[7],
          ratesType: queryKey[8],
        },
      }),
  })

  const rates = computed(() => data?.value?.data)

  return { isRatesLoading: isLoading, rates, isRatesFetching: isFetching }
}

export function useSuggestions() {
  const { isLoading, data, isFetching } = useQuery({
    queryKey: ['RISuggestions', selectedAccommodationId],
    queryFn: ({ queryKey }) =>
      apis.rateInsights.getSuggestions({
        accommodationId: queryKey[1],
      }),
  })

  const suggestions = computed(() => data?.value?.data)
  return { isSuggestionsLoading: isLoading, suggestions, isSuggestionsFetching: isFetching }
}

export function useCompetitorsUpdateMutation() {
  const queryClient = useQueryClient()
  const {
    isLoading: competitorsUpdateLoading,
    isSuccess: competitorsUpdateSuccess,
    isError: competitorsUpdateError,
    mutate: competitorsUpdate,
  } = useMutation({
    mutationFn: ({ accommodationId, competitors }) =>
      apis.rateInsights.updateSelectedCompetitors({ accommodationId, competitors }),
    onSuccess: (_data, variables) => {
      queryClient.setQueryData(['RICompetitors', variables.accommodationId], old => ({
        ...old,
        data: variables.competitors.map(id => ({ id })),
      }))
      queryClient.invalidateQueries({ queryKey: ['RICompetitors'], refetchInactive: true })
      queryClient.invalidateQueries({ queryKey: ['RISelectedBookingSites'] })
      queryClient.invalidateQueries({ queryKey: ['Notifications'] })
    },
  })

  return {
    competitorsUpdateLoading,
    competitorsUpdateSuccess,
    competitorsUpdateError,
    competitorsUpdate,
  }
}

export function useSelectedBookingSitesUpdateMutation() {
  const queryClient = useQueryClient()
  const {
    isLoading: bookingSitesUpdateLoading,
    isSuccess: bookingSitesUpdateSuccess,
    isError: bookingSitesUpdateError,
    mutate: bookingSitesUpdate,
  } = useMutation({
    mutationFn: ({ accommodationId, bookingSites }) =>
      apis.rateInsights.updateBookingSites({ accommodationId, bookingSites }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['RISelectedBookingSites'], refetchInactive: true })
    },
  })

  return {
    bookingSitesUpdateLoading,
    bookingSitesUpdateSuccess,
    bookingSitesUpdateError,
    bookingSitesUpdate,
  }
}
